/**
 * Personal Mode Clone Database Limit Message
 *
 * Shows a message when Personal mode restricts cloning with data due to large database.
 * Explains the limit and provides an upgrade option. Schema-only cloning remains available.
 */

import { AlertTriangle, Sparkles, Database, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PersonalModeLimitCheck } from '../utils/personalModeLimits';
import { PERSONAL_MODE_MAX_SIZE_BYTES, formatBytes } from '../utils/personalModeLimits';

type PersonalModeCloneDatabaseLimitMessageProps = {
  limitCheck: PersonalModeLimitCheck;
  onUpgrade: () => void;
  onContinueSchemaOnly: () => void;
  onCancel: () => void;
  schemaOnlyAvailable?: boolean;
  schemaOnlyUnavailableMessage?: string;
};

/**
 * Message shown when database exceeds Personal mode size limits for cloning with data.
 * Offers upgrade prompt, schema-only option, and cancel option.
 */
export function PersonalModeCloneDatabaseLimitMessage({
  limitCheck,
  onUpgrade,
  onContinueSchemaOnly,
  onCancel,
  schemaOnlyAvailable = true,
  schemaOnlyUnavailableMessage,
}: PersonalModeCloneDatabaseLimitMessageProps) {
  const limitString = formatBytes(PERSONAL_MODE_MAX_SIZE_BYTES);

  return (
    <div className="flex flex-col items-center justify-center p-6" data-testid="personal-mode-clone-database-limit">
      <div className="max-w-md text-center space-y-5">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="p-3 rounded-full bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
            <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>

        {/* Title */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Large Database Detected
          </h3>
          <p className="text-sm text-muted-foreground">
            {limitCheck.description}
          </p>
        </div>

        {/* Limit explanation */}
        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Personal Mode Limit
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Personal mode limits cloning with data to databases {limitString} or smaller.
            {schemaOnlyAvailable ? ' Schema-only cloning is always available.' : null}
          </p>
          {!schemaOnlyAvailable && schemaOnlyUnavailableMessage ? (
            <p className="text-sm text-muted-foreground mt-2">{schemaOnlyUnavailableMessage}</p>
          ) : null}
        </div>

        {/* Schema-only option */}
        {schemaOnlyAvailable ? (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Copy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                Schema-Only Clone
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Clone the database structure without data. You can migrate data separately.
            </p>
            <Button
              variant="outline"
              onClick={onContinueSchemaOnly}
              className="w-full"
              data-testid="schema-only-button"
            >
              Continue with Schema Only
            </Button>
          </div>
        ) : null}

        {/* Upgrade prompt */}
        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              Upgrade to Pro
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Clone databases of any size with data with Pro.
          </p>
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={onUpgrade} className="gap-2" data-testid="upgrade-button">
              <Sparkles className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
